import { locatedAnchor } from '../../../shared/routeAnchor';
import { locatedStop } from '../../../shared/routeRetention';
import type { BusinessType, SignalKind, SignalSource } from '../../../shared/enums';
import {
  AGENDA_CITIES,
  ANAHEIM_LICENSES_URL,
  ANAHEIM_PERMITS_URL,
  CITY_LIST_URLS,
  FBN_SEARCH_URL,
  OCHCA_PLAN_REVIEW_URL,
  abcLicenseUrl,
} from '../../../shared/fields';
import type { IsoDate, Lead, Signal } from '../../../shared/types';
import type { RouteStop, StoredRoute } from '../data/corridor';
import type { LeadInput, LeadPatch } from '../data/store';
import { SIGNAL_EVENT_LABELS, SIGNAL_SOURCE_LABELS } from '../ui/labels';
import { formatDate } from './dates';
import { fold } from './pipeline';
import type { LeadLocation } from './followupRoute';
import { orderStops } from './route';

/** Pure helpers for the New businesses inbox (D57). */

/** The full street address a map search needs; null without a street (a city alone is no stop). */
export const signalAddress = (s: Signal): string | null =>
  s.address ? [s.address, s.city, s.zip ? `CA ${s.zip}` : 'CA'].filter(Boolean).join(', ') : null;

/** Where the row itself says the business is (D80): Google's place and coordinates from the daily
 *  enrichment, so no lookup is needed; null until it has them. */
export const locationOf = (s: Signal): LeadLocation | null =>
  typeof s.lat === 'number' && typeof s.lng === 'number' && Number.isFinite(s.lat) && Number.isFinite(s.lng)
    ? { place_id: s.place_id ?? `signal:${s.id}`, lat: s.lat, lng: s.lng, address: signalAddress(s) }
    : null;

/** Each source's own page when a signal has no recorded link: the ABC license, OC Health's search
 *  (a plan check imported without its record id), Anaheim's list (no page per license), the agenda
 *  search of the city an agenda item's id names (D64). */
const agendaSearch = (id: string): string => {
  const city = id.split(':')[1] ?? '';
  return `https://${city in AGENDA_CITIES ? city : 'newportbeach'}.legistar.com/Legislation.aspx`;
};
const FALLBACK_URL: Record<SignalSource, (ref: string, id: string) => string> = {
  abc: abcLicenseUrl,
  ochca: () => OCHCA_PLAN_REVIEW_URL,
  anaheim: () => ANAHEIM_LICENSES_URL,
  huntington_beach: () => CITY_LIST_URLS.huntington_beach,
  yorba_linda: () => CITY_LIST_URLS.yorba_linda,
  la_habra: () => CITY_LIST_URLS.la_habra,
  costa_mesa: () => CITY_LIST_URLS.costa_mesa,
  laguna_beach: () => CITY_LIST_URLS.laguna_beach,
  irvine: () => CITY_LIST_URLS.irvine,
  newport_beach: () => CITY_LIST_URLS.newport_beach,
  lake_forest: () => CITY_LIST_URLS.lake_forest,
  fullerton: () => CITY_LIST_URLS.fullerton,
  orange: () => CITY_LIST_URLS.orange,
  tustin: () => CITY_LIST_URLS.tustin,
  westminster: () => CITY_LIST_URLS.westminster,
  brea: () => CITY_LIST_URLS.brea,
  cypress: () => CITY_LIST_URLS.cypress,
  fountain_valley: () => CITY_LIST_URLS.fountain_valley,
  stanton: () => CITY_LIST_URLS.stanton,
  seal_beach: () => CITY_LIST_URLS.seal_beach,
  san_clemente: () => CITY_LIST_URLS.san_clemente,
  los_alamitos: () => CITY_LIST_URLS.los_alamitos,
  fbn: () => FBN_SEARCH_URL,
  city_agendas: (_ref, id) => agendaSearch(id),
  anaheim_permits: () => ANAHEIM_PERMITS_URL,
};

/** A monthly list's signals carry its month ("July 2026") instead of a license number (D61). */
const MONTH_REF = /^[A-Z][a-z]+ \d{4}$/;

/** An HdL city's license search: no page per license, and no link that fills the search (D62). */
const HDL_SEARCH = /^https:\/\/[a-z]+\.hdlgov\.com\//;

/** Where the information came from, with a link to the official record when the source has one;
 *  on an HdL search, the account # to copy for its Account # box. */
export const signalSource = (s: Signal): { text: string; url: string; copy?: string } => {
  const month = MONTH_REF.test(s.ref);
  const url = s.source_url?.startsWith('https://') ? s.source_url : FALLBACK_URL[s.source](s.ref, s.id);
  return {
    text: `${SIGNAL_SOURCE_LABELS[s.source]}${month ? ', ' : ' #'}${s.ref}`,
    url,
    ...(HDL_SEARCH.test(url) && !month ? { copy: s.ref } : {}),
  };
};

/** The sources in the order the inbox lists them, by a short name. */
export const SOURCE_ORDER: SignalSource[] = [
  'abc',
  'anaheim',
  'irvine',
  'orange',
  'tustin',
  'fullerton',
  'newport_beach',
  'huntington_beach',
  'yorba_linda',
  'la_habra',
  'lake_forest',
  'costa_mesa',
  'laguna_beach',
  'westminster',
  'brea',
  'cypress',
  'fountain_valley',
  'stanton',
  'seal_beach',
  'san_clemente',
  'los_alamitos',
  'city_agendas',
  'anaheim_permits',
  'fbn',
  'ochca',
];
export const SOURCE_NAMES: Record<SignalSource, string> = {
  abc: 'ABC licenses',
  ochca: 'OC Health plan checks',
  anaheim: 'Anaheim',
  huntington_beach: 'Huntington Beach',
  yorba_linda: 'Yorba Linda',
  la_habra: 'La Habra',
  costa_mesa: 'Costa Mesa',
  laguna_beach: 'Laguna Beach',
  irvine: 'Irvine',
  newport_beach: 'Newport Beach',
  lake_forest: 'Lake Forest',
  fullerton: 'Fullerton',
  orange: 'Orange',
  tustin: 'Tustin',
  westminster: 'Westminster',
  brea: 'Brea',
  cypress: 'Cypress',
  fountain_valley: 'Fountain Valley',
  stanton: 'Stanton',
  seal_beach: 'Seal Beach',
  san_clemente: 'San Clemente',
  los_alamitos: 'Los Alamitos',
  fbn: 'County filings',
  city_agendas: 'City agendas',
  anaheim_permits: 'Anaheim permits',
};
/** These publish a list per month; their date is the month's (D61). */
export const MONTHLY = new Set<SignalSource>([
  'yorba_linda',
  'la_habra',
  'lake_forest',
  'costa_mesa',
  'laguna_beach',
]);
/** The rep brings these in: by upload (D61) or the Send to CRM bookmark (D58); the server reads the rest. */
export const UPLOADED = new Set<SignalSource>(['costa_mesa', 'laguna_beach']);
/** HdL cities behind Cloudflare: their license search comes in with the Send to CRM bookmark (D62). */
export const BOOKMARKED = new Set<SignalSource>([
  'westminster',
  'brea',
  'cypress',
  'fountain_valley',
  'stanton',
  'seal_beach',
  'san_clemente',
  'los_alamitos',
]);

export const monthName = (day: IsoDate): string =>
  new Date(`${day.slice(0, 7)}-01T12:00:00Z`).toLocaleString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

/** What happened, in the source's words: a new ABC license, a new build on an OC Health plan check… */
export const signalEventLabel = (s: Signal): string =>
  s.source === 'ochca' && s.event === 'new_business' ? 'New build' : SIGNAL_EVENT_LABELS[s.event];

const BUSINESS_TYPE_FOR: Record<SignalKind, BusinessType> = {
  food_drink: 'restaurant',
  retail: 'retail',
  food_truck: 'other',
  service: 'other',
};

/** A new lead for the business. The owner goes to the notes, not `owner_name`: it is usually an LLC,
 *  and `owner_name` is who the emails greet (D32). */
/** The API's lead limits (api/src/schemas.ts): a longer value would be refused and the lead lost (D44). */
const LIMIT = { business_name: 120, address: 200, neighborhood: 80, notes: 4000 } as const;
const fit = (v: string | null, max: number): string | null => (v && v.length > max ? v.slice(0, max) : v);

/** The record in one notes line: what, from where and when, the license or plan, owner and contact
 *  (a plan check's contact, or the people on a license with the website and mailing address, D59). */
const recordLine = (s: Signal, today: IsoDate): string =>
  [
    `${signalEventLabel(s)} (${signalSource(s).text}, filed ${formatDate(s.filed_on, today)})`,
    s.detail,
    s.owner_name ? `Owner: ${s.owner_name}` : null,
    s.contact_name ? `${s.source === 'ochca' ? 'Plan contact' : 'On the license'}: ${s.contact_name}` : null,
    s.contact_website ? `Website: ${s.contact_website}` : null,
    s.contact_address ? `Mailing address: ${s.contact_address}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

/** Keep public street/locality separate for Census; never carry a signal's Google identity. */
const signalLeadAddress = (s: Signal) => ({
  address: fit(s.address, LIMIT.address),
  address_city: fit(s.city?.trim() || null, LIMIT.neighborhood),
  address_postal_code: /^\d{5}$/.test(s.zip?.trim() ?? '') ? s.zip!.trim() : null,
});

export const signalToLead = (s: Signal, today: IsoDate): LeadInput => ({
  business_name: fit(s.business_name, LIMIT.business_name) as string,
  business_type: BUSINESS_TYPE_FOR[s.kind],
  ...signalLeadAddress(s),
  neighborhood: fit(s.city, LIMIT.neighborhood),
  // the POS its website runs on, when the fingerprint read it (D79)
  current_pos: s.detected_pos ?? 'unknown',
  notes: fit(recordLine(s, today), LIMIT.notes),
  // the record's contact moves into the lead (D58, D59); names stay in the notes — a plan check's is
  // often an expediter, a license's an LLC's members — not who the emails should greet
  ...(s.contact_phone ? { phone: s.contact_phone } : {}),
  ...(s.contact_email ? { email: s.contact_email } : {}),
});

/** "Add as lead" on a business the rep already has: fill missing address/contact fields, never
 *  overwrite what was typed, and add the record to the notes once. Null when there is nothing to add. */
export const mergeSignalIntoLead = (lead: Lead, s: Signal, today: IsoDate): LeadPatch | null => {
  const patch: LeadPatch = {};
  // Any address identity change invalidates even manual coordinates in the local and server writers.
  if (lead.coordinate_source !== 'manual') {
    const address = signalLeadAddress(s);
    for (const key of ['address', 'address_city', 'address_postal_code'] as const) {
      if (!lead[key] && address[key]) patch[key] = address[key];
    }
  }
  if (s.contact_phone && !lead.phone) patch.phone = s.contact_phone;
  if (s.contact_email && !lead.email) patch.email = s.contact_email;
  if (!lead.notes?.includes(signalSource(s).text)) {
    const leftOver = [
      s.contact_phone && !patch.phone && s.contact_phone !== lead.phone ? s.contact_phone : null,
      s.contact_email && !patch.email && s.contact_email !== lead.email ? s.contact_email : null,
    ];
    const line = [recordLine(s, today), ...leftOver].filter(Boolean).join(' · ');
    patch.notes = fit([lead.notes, line].filter(Boolean).join('\n'), LIMIT.notes);
  }
  return Object.keys(patch).length ? patch : null;
};

const normalize = (name: string): string =>
  fold(name)
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Whether a lead is in `city`: its neighborhood is the city, or its address names the city as its
 *  own part ("9 Main St, Orange, CA", or typed by hand "9 Main St Orange CA 92866") — not a street
 *  that happens to share the name. Null when the address is a street alone: nothing to tell. */
const inCity = (l: Lead, city: string): boolean | null => {
  if (!l.neighborhood && !l.address) return null; // nothing to tell
  const c = fold(city);
  if (l.neighborhood && fold(l.neighborhood) === c) return true;
  if (!l.address) return false; // the neighborhood names another city
  const address = fold(l.address);
  if (new RegExp(`(^|[\\s,])${escapeRe(c)}(\\s*,|\\s+ca\\b|\\s*$)`).test(address)) return true;
  if (/,\s*[a-z .'-]+,?\s*ca\b/.test(address)) return false; // ", Irvine, CA": some other city
  return l.neighborhood ? false : null;
};

/** A lead the rep already has for this business: same name (accents and punctuation aside), and
 *  nothing says it is in another city. */
export const matchingLead = (s: Signal, leads: readonly Lead[]): Lead | undefined => {
  const name = normalize(s.business_name);
  return leads.find((l) => normalize(l.business_name) === name && (!s.city || inCity(l, s.city) !== false));
};

export interface SignalPlan {
  route: StoredRoute;
  /** Picks that could not be placed (no street address, or the lookup found nothing). */
  unlocated: Signal[];
}

/** The day route through the picked businesses, nearest-first from `anchor` (D26), each stop's
 *  note saying why it is on the route. A pick that knows where it is needs no entry in `located`.
 *  Stops carry no lead but their signal: logging a visit there creates the lead and marks the signal. */
export const planSignalRoute = (
  picks: readonly Signal[],
  located: ReadonlyMap<string, LeadLocation>,
  anchor: StoredRoute['anchor'],
  createdAt: string,
): SignalPlan => {
  const stops: RouteStop[] = [];
  const unlocated: Signal[] = [];
  for (const s of picks) {
    const at = located.get(s.id) ?? locationOf(s);
    if (!at) {
      unlocated.push(s);
      continue;
    }
    stops.push({
      place_id: at.place_id ?? `signal:${s.id}`,
      name: s.business_name,
      address: at.address ?? signalAddress(s),
      lat: at.lat,
      lng: at.lng,
      types: [],
      detected_pos: s.detected_pos ?? 'unknown',
      signal_id: s.id,
      note: [signalEventLabel(s), s.detail].filter(Boolean).join(' · '),
    });
  }
  const start = locatedAnchor({ anchor, stops });
  const ordered = start ? orderStops(start, stops.filter(locatedStop)).map((o) => o.stop) : stops;
  return { route: { anchor, stops: ordered, built: true, created_at: createdAt }, unlocated };
};
